<template>
    <v-dialog v-model="visible" persistent max-width="900px">
        <v-card light>
            <v-card-title class="justify-center">
                <v-spacer/>
                <span class="text-h5">
                    {{ form.id ? 'Edição' : 'Cadastro' }} de Vídeo de Ajuda
                </span>
                <v-spacer/>
            </v-card-title>

            <v-card-text>
                <v-row>
                    <!-- Imagem de capa (opcional) -->
                    <v-col cols="12" md="4">
                        <div class="caption grey--text text--darken-1 mb-1">Imagem de Capa (opcional)</div>
                        <div
                            class="cover-upload"
                            :style="coverStyle"
                            @click="triggerCoverInput"
                            @dragover.prevent="coverDragging = true"
                            @dragleave.prevent="coverDragging = false"
                            @drop.prevent="onCoverDrop"
                            :class="{ 'drag-over': coverDragging }"
                        >
                            <div v-if="!coverPreview" class="cover-placeholder">
                                <v-icon large color="grey lighten-2">mdi-image-plus</v-icon>
                                <p class="caption grey--text mt-1 mb-0">Clique ou arraste uma imagem</p>
                            </div>
                            <v-progress-circular
                                v-if="coverUploading"
                                indeterminate
                                color="white"
                                class="cover-uploading"
                            />
                            <v-btn
                                v-if="coverPreview && !coverUploading"
                                x-small icon
                                color="grey darken-2"
                                class="cover-remove-btn"
                                @click.stop="removeCover"
                            >
                                <v-icon x-small>mdi-close</v-icon>
                            </v-btn>
                        </div>
                        <input
                            ref="coverInput"
                            type="file"
                            accept="image/*"
                            style="display: none;"
                            @change="onCoverSelected"
                        />
                        <p class="caption grey--text mt-1 mb-0">
                            Se não informada, usa a miniatura do YouTube.
                        </p>
                    </v-col>

                    <!-- Título + URL + Ordem + Status -->
                    <v-col cols="12" md="8">
                        <v-text-field
                            label="Título"
                            v-model="form.title"
                            dense
                            outlined
                            class="mb-3"
                            hide-details
                        />

                        <v-text-field
                            label="URL do vídeo (YouTube)"
                            v-model="form.url"
                            dense
                            outlined
                            class="mb-3"
                            placeholder="https://www.youtube.com/watch?v=..."
                            hint="Cole o link do YouTube; será convertido para o formato de incorporação."
                            persistent-hint
                            @blur="normalizarUrl"
                        />

                        <div v-if="embedPreview" class="preview-frame mb-3">
                            <iframe
                                :src="embedPreview"
                                frameborder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowfullscreen
                            />
                        </div>

                        <v-select
                            label="Categoria"
                            v-model="form.categoria_id"
                            :items="categorias"
                            item-text="nome"
                            item-value="id"
                            dense
                            outlined
                            clearable
                            hide-details
                            class="mb-3"
                            placeholder="Sem categoria"
                            no-data-text="Nenhuma categoria cadastrada"
                        />

                        <v-textarea
                            label="Descrição (opcional)"
                            v-model="form.descricao"
                            dense
                            outlined
                            rows="3"
                            auto-grow
                            hide-details
                            class="mb-3"
                        />

                        <v-row>
                            <v-col cols="6">
                                <v-text-field
                                    label="Ordem"
                                    v-model.number="form.ordem"
                                    type="number"
                                    dense
                                    outlined
                                    hide-details
                                />
                            </v-col>
                            <v-col cols="6" class="d-flex align-center">
                                <v-switch
                                    v-model="ativo"
                                    inset
                                    hide-details
                                    class="mt-0 pt-0"
                                    :label="ativo ? 'Ativo' : 'Inativo'"
                                />
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
            </v-card-text>

            <v-card-actions>
                <v-btn text color="warning" @click="$emit('close')">Cancelar</v-btn>
                <v-spacer/>
                <v-btn color="success" @click="salvar" :loading="loading">
                    <v-icon>mdi-content-save</v-icon>&nbsp;Salvar
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'VideoAjuda',

    props: {
        visible: { type: Boolean, default: false },
        formData: { type: Object, default: Object },
        categorias: { type: Array, default: () => [] },
    },

    data: () => ({
        form: {},
        loading: false,

        // Capa
        coverPreview:   null,
        coverUploading: false,
        coverDragging:  false,
    }),

    computed: {
        coverStyle() {
            return this.coverPreview
                ? { backgroundImage: `url(${this.coverPreview})` }
                : {};
        },

        ativo: {
            get() {
                return this.form.status !== 'inativo';
            },
            set(value) {
                this.$set(this.form, 'status', value ? 'ativo' : 'inativo');
            },
        },

        embedPreview() {
            const id = this.youtubeId(this.form.url);
            return id ? `https://www.youtube.com/embed/${id}` : '';
        },
    },

    mounted() {
        this.form = this.formData;
        this.initCoverPreview();
    },

    watch: {
        formData(value) {
            this.form = value ? JSON.parse(JSON.stringify(value)) : {};
            this.initCoverPreview();
        },
    },

    methods: {
        // ── URL do YouTube ───────────────────────────────────────────────────
        youtubeId(url) {
            if (!url) return '';
            const match = url.match(/(?:embed\/|v=|youtu\.be\/|shorts\/)([A-Za-z0-9_-]{6,})/);
            return match ? match[1] : '';
        },

        normalizarUrl() {
            const id = this.youtubeId(this.form.url);
            if (id) {
                this.form.url = `https://www.youtube.com/embed/${id}`;
            }
        },

        // ── Imagem de capa ───────────────────────────────────────────────────
        initCoverPreview() {
            if (this.form.imagem && this.form.imagem.path) {
                this.coverPreview = `${this.$urlAPI}/videos-de-ajuda/files/${this.form.imagem.path}`;
            } else {
                this.coverPreview = null;
            }
        },

        triggerCoverInput() {
            this.$refs.coverInput.click();
        },

        onCoverDrop(e) {
            this.coverDragging = false;
            const file = e.dataTransfer.files && e.dataTransfer.files[0];
            if (file) this.uploadCover(file);
        },

        onCoverSelected(e) {
            const file = e.target.files && e.target.files[0];
            if (file) this.uploadCover(file);
            e.target.value = '';
        },

        uploadCover(file) {
            const reader = new FileReader();
            reader.onload = (ev) => { this.coverPreview = ev.target.result; };
            reader.readAsDataURL(file);

            this.coverUploading = true;
            const fd = new FormData();
            fd.append('file', file);

            this.$http.post('files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
                .then(resp => {
                    this.$set(this.form, 'imagem', { path: resp.data.path, name: resp.data.name });
                })
                .catch(() => {
                    this.notify('Erro ao enviar imagem', 'error');
                    this.coverPreview = null;
                })
                .finally(() => { this.coverUploading = false; });
        },

        removeCover() {
            this.$set(this.form, 'imagem', null);
            this.coverPreview = null;
        },

        // ── Salvar ───────────────────────────────────────────────────────────
        salvar() {
            this.normalizarUrl();

            if (!this.form.title || !this.form.url) {
                this.notify('Informe o título e a URL do vídeo', 'warning');
                return;
            }

            const id = this.form.id;

            if (id) {
                if (!this.checkPermission('zapercontrol_videos_ajuda_atualizar')) {
                    alert('Usuário sem permissão');
                    return;
                }
                this.loading = true;
                this.$http.put(`videos-de-ajuda/${id}`, this.form)
                    .then(() => { this.$emit('onCadastro'); })
                    .catch(e => { alert(e); })
                    .finally(() => (this.loading = false));
                return;
            }

            if (!this.checkPermission('zapercontrol_videos_ajuda_adicionar')) {
                alert('Usuário sem permissão');
                return;
            }

            this.loading = true;
            this.$http.post('videos-de-ajuda', this.form)
                .then(() => { this.$emit('onCadastro'); })
                .catch(e => { alert(e); })
                .finally(() => (this.loading = false));
        },
    },
}
</script>

<style scoped>
.v-card {
    padding: 0 15px 0 15px;
}

.cover-upload {
    width: 100%;
    height: 150px;
    border: 2px dashed #ccc;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #fafafa;
    background-size: cover;
    background-position: center;
    position: relative;
    transition: border-color 0.2s;
    overflow: hidden;
}

.cover-upload.drag-over {
    border-color: #1976d2;
    background-color: #e3f2fd;
}

.cover-placeholder {
    text-align: center;
    pointer-events: none;
}

.cover-uploading {
    position: absolute;
}

.cover-remove-btn {
    position: absolute;
    top: 6px;
    right: 6px;
    background: rgba(255,255,255,0.85) !important;
}

.preview-frame iframe {
    width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: 8px;
}
</style>
